import { FC, useState, useRef, useEffect } from "react";
import { useRouter } from "next/router";
import { Box, Flex, Icon, Container } from "@smile2impress/components/relaunch";
import { useColorMode } from "@smile2impress/themes";
import { UTSLocale } from "@smile2impress/attraction-constants";
import { safeSessionStorage } from "@smile2impress/attraction-utils";
import { getPlatformRelatedValue } from "layouts/relaunch/RelaunchLayout/lib";
import { useUTSLocaleContext } from "context/utsLocaleContext";
import { useCampaignContext } from "context/campaignContext";

import { UrlsConfig } from "configs/urlsConfig";
import Link from "components/Link";
import { Logo } from "components/Logo";
import { ExtraLogo } from "components/Logo/components/ExtraLogo";
import { SESSION_STORAGE_KEYS } from "constants/enum";
import { HeaderBanner } from "components/PromoCampaigns/components/HeaderBanner";

import { useLocationSelect } from "./hooks/useLocationSelect";
import { HeaderProps } from "./Header.types";
import { DesktopHeader, MobileMenu } from "./components";
import { HeaderContentBar } from "./components/HeaderContentBar";
import { MobileHeader } from "./components/MobileHeader";

export const Header: FC<HeaderProps> = ({
    content,
    expandableMobileMenu,
    expandableMessengerButton,
    hideMenu = false,
    logoAsLink = true,
    hasFixedBanner = false,
}) => {
    const { pathname } = useRouter();
    const { locale } = useUTSLocaleContext();
    const [isFixedPosition, setIsFixedPosition] = useState(false);
    const [quizResultOption, setQuizResultOption] = useState(0);
    const { campaign } = useCampaignContext();
    const hasBannerContent = !!campaign;

    const parentRef = useRef<HTMLDivElement | null>(null);
    const menuPlaceholderRef = useRef(null);
    const locationSelect = useLocationSelect();

    const { candidate, logoLink } = content;

    const { mobile: hideMenuForMobile, desktop: hideMenuForDesktop } =
        getPlatformRelatedValue(hideMenu);

    const withExtraLogo = locale !== UTSLocale.FRANCE_FR;
    const isSmileVisionOfferPage = pathname.startsWith(
        UrlsConfig.SMILE_VISION_OFFER
    );

    useEffect(() => {
        const quizResult = Number(
            safeSessionStorage.getItem(
                SESSION_STORAGE_KEYS.IMPRESS_QUIZ_RESULT_OPTION
            ) || "1"
        );

        setQuizResultOption(quizResult);

        const handleScroll = () => {
            if (!parentRef.current) return;

            const parentRect = parentRef.current.getBoundingClientRect();
            const topOffsetRef = hasBannerContent && hasFixedBanner ? 65 : 25;

            setIsFixedPosition(parentRect.top < topOffsetRef);
        };

        window.addEventListener("scroll", handleScroll);

        return () => window.removeEventListener("scroll", handleScroll);
    }, [parentRef?.current, hasBannerContent, hasFixedBanner]);

    const { colorMode } = useColorMode();

    const logoContent = (
        <Flex direction="row" as="span" justifyItems="flex-start">
            <Logo isRelaunch />
            {withExtraLogo && (
                <Icon
                    display={["none", "none", "block"]}
                    ml="8px"
                    mt="15px"
                    w="140px"
                    h="28px"
                    as={ExtraLogo}
                    color="base.logo"
                    preserveAspectRatio="xMinYMax meet"
                    sx={
                        colorMode === "dark"
                            ? {
                                  "path[fill='#012DF3']": {
                                      fill: "#FFFFFF !important",
                                  },
                              }
                            : {}
                    }
                />
            )}
        </Flex>
    );

    return (
        <>
            <HeaderBanner isFixedPosition={hasFixedBanner} />
            <Box position="relative" ref={parentRef}>
                <Container
                    bgColor="bg.base"
                    mt={[0, 0, 2]}
                    h={["62px", "62px", 4]}
                    mb={[0, 0, 4]}
                    zIndex="sticky"
                    {...(!!quizResultOption
                        ? {}
                        : {
                              boxShadow: [
                                  "0px 1px 4px 0px rgba(0, 0, 0, 0.05)",
                                  "0px 1px 4px 0px rgba(0, 0, 0, 0.05)",
                                  "initial",
                              ],
                          })}
                >
                    <HeaderContentBar
                        isCentered={isSmileVisionOfferPage}
                        logo={
                            // TODO AD-2185: logo non-clickable for Denmark
                            logoAsLink && locale !== UTSLocale.DENMARK_DK ? (
                                <Link href={logoLink}>{logoContent}</Link>
                            ) : (
                                logoContent
                            )
                        }
                        rightSlot={
                            <MobileHeader
                                candidate={candidate}
                                locationSelect={locationSelect}
                                hideMenu={hideMenuForMobile}
                            />
                        }
                        menuPlaceholderRef={menuPlaceholderRef}
                    />
                </Container>
                {!hideMenuForMobile && (
                    <Box display={["block", "block", "none"]}>
                        <MobileMenu
                            {...content}
                            expandableMobileMenu={expandableMobileMenu}
                            expandableMessengerButton={
                                expandableMessengerButton
                            }
                        />
                    </Box>
                )}
                <Box display={["none", "none", "block"]}>
                    <DesktopHeader
                        {...content}
                        candidate={candidate}
                        locationSelect={locationSelect}
                        menuPlaceholderRef={menuPlaceholderRef}
                        isFixedPosition={isFixedPosition}
                        hasFixedBanner={hasBannerContent && hasFixedBanner}
                        hideMenu={hideMenuForDesktop}
                    />
                </Box>
            </Box>
        </>
    );
};
